import * as React from "react";
import { VFC, memo, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Link } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Checkbox from "@mui/material/Checkbox";
import { DeviceSearchResponse } from "../../terminal/api/TerminalForm";
import { Tag } from "../../../entity/Tag";
import Constants from "../../../utils/Constants";
import { Utility } from "../../../utils/Utility";
import { UserContext } from "../../../UserProvider";
import { DeviceUiConverter } from "../../../converter/DeviceUiConverter";
import SimTypeBox from "../box/SimTypeBox";

const contentFontSize = "0.875rem";

const style = {
  tableHeader: {
    "& th": {
      backgroundColor: "#fff",
      zIndex: 1,
      padding: "16px 8px",
      whiteSpace: "nowrap",
    },
    "& th:nth-of-type(1)": {
      width: "40px",
    },
  },
  tableRow: {
    "& td": {
      color: "black",
      backgroundColor: "#fff",
      padding: "4px 8px",
      justifyContent: "around"
    },
    "&:nth-of-type(odd) td": {
      backgroundColor: "#fafafa",
    },
    "& td:nth-of-type(1)": {
      width: "40px",
    },
  },
};

// ヘッダー
interface HeadItem {
  key: keyof DeviceSearchResponse;
  value: string;
  isSort?: Boolean;
}
// データ
interface Device {
  imei: string;
  tag: string;
  deviceUser: string;
  model: string;
}

const label = { inputProps: { "aria-label": "Checkbox demo" } };

const headItem: HeadItem[] = [
  { key: "tag", value: "タグ", isSort: true },
  { key: "label", value: "ラベル", isSort: true },
  { key: "imei", value: "IMEI/シリアルナンバー", isSort: true },
  { key: "telNumber", value: "電話番号", isSort: true },
  { key: "model", value: "機種", isSort: true },
  { key: "deviceName", value: "デバイス名", isSort: true },
  { key: "simType", value: "SIM", isSort: true },
  { key: "carrier", value: "通信事業者", isSort: true },
  { key: "isOnline", value: "オンライン", isSort: true },
  { key: "deviceUser", value: "端末利用者", isSort: true },
  { key: "simTraffic", value: "SIM通信量", isSort: true },
  { key: "sim2Traffic", value: "SIM2通信量", isSort: true },
  { key: "esimTraffic", value: "eSIM通信量", isSort: true },
  { key: "firmware", value: "F/W Ver", isSort: true },
  { key: "tenantName", value: "テナント", isSort: true },
  { key: "createDate", value: "登録日時", isSort: true },
];

/***************************************************************
 * ソート
 **************************************************************/
export type Order = "asc" | "desc";
function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (orderBy === "tag") {
    const b2 = (b[orderBy] as Tag[]).map((tag) => tag.tagName).join(", ");
    const a2 = (a[orderBy] as Tag[]).map((tag) => tag.tagName).join(", ");
    if (b2 < a2) {
      return -1;
    }
    if (b2 > a2) {
      return 1;
    }
  }
  const b3 = b[orderBy] ?? "";
  const a3 = a[orderBy] ?? "";
  if (b3 < a3) {
    return -1;
  }
  if (b3 > a3) {
    return 1;
  }
  return 0;
}
function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: string | Tag[] },
  b: { [key in Key]: string | Tag[] }
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}
function stableSort<T>(
  array: readonly T[],
  comparator: (a: T, b: T) => number
) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

interface EnhancedTableProps {
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof DeviceSearchResponse
  ) => void;
  order: Order;
  orderBy: string;
  checkedSome: boolean;
  checkedAll: boolean;
  disabled: boolean;
  handleCheckAll: (
    event: React.MouseEvent<HTMLButtonElement>,
    checkedSome: boolean
  ) => void;
}

// ヘッダー
const Head = (props: EnhancedTableProps) => {
  const {
    order,
    orderBy,
    onRequestSort,
    checkedSome,
    checkedAll,
    disabled,
    handleCheckAll,
  } = props;
  const createSortHandler =
    (property: keyof DeviceSearchResponse) =>
    (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead sx={style.tableHeader}>
      <TableRow>
        <TableCell style={{ position: "sticky", left: 0 }}>
          <Checkbox
            {...label}
            indeterminate={checkedSome}
            checked={checkedAll}
            disabled={disabled}
            onClick={(e) => handleCheckAll(e, checkedSome)}
          />
        </TableCell>
        {headItem.map((header, index) => (
          <TableCell key={index}>
            {header.isSort ? (
              <TableSortLabel
                active={orderBy === header.key}
                direction={orderBy === header.key ? order : "asc"}
                onClick={createSortHandler(header.key)}
              >
                {header.value}
              </TableSortLabel>
            ) : (
              header.value
            )}
          </TableCell>
        ))}
        <TableCell
          key={headItem.length}
          style={{ position: "sticky", right: 0 }}
        ></TableCell>
      </TableRow>
    </TableHead>
  );
};

export type PageState = {
  checked: { [index: string]: boolean };
  page: number;
  rowsPerPage: number;
  order: Order;
  orderBy: keyof DeviceSearchResponse;
};

type Props = {
  deviceList: DeviceSearchResponse[];
  pageState: PageState;
  setPageState: (value: PageState) => void;
  sliceOnePage: (
    deviceList: DeviceSearchResponse[],
    pageState: PageState
  ) => DeviceSearchResponse[];
};

export const DeviceTable: VFC<Props> = memo(({
  deviceList,
  pageState,
  setPageState,
  sliceOnePage,
}) => {
  const navigate = useNavigate();

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof DeviceSearchResponse
  ) => {
    const isAsc = pageState.orderBy === property && pageState.order === "asc";
    setPageState(cache.value.pageState = { ...pageState, order: isAsc ? "desc" : "asc", orderBy: property, page: 0 });
  };

  const cache = useContext(UserContext) as {
    value: { pageState: PageState }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPageState(cache.value.pageState = { ...pageState, page: newPage });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPageState(cache.value.pageState = {
      ...pageState,
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0,
    });
  };
  const detailBtnClick = (id: string) => {
    sessionStorage.setItem("DEVICE_ID", id);
    navigate("/terminal_detail");
  };
  const simTrafficClick = (id: string) => {
    sessionStorage.setItem("DEVICE_ID", id);
    sessionStorage.setItem("SIM_TYPE", "0");
    navigate("/terminal_traffic_stats");
  }
  const sim2TrafficClick = (id: string) => {
    sessionStorage.setItem("DEVICE_ID", id);
    sessionStorage.setItem("SIM_TYPE", "2");
    navigate("/terminal_traffic_stats");
  }
  const esimTrafficClick = (id: string, modelName: string) => {
    sessionStorage.setItem("DEVICE_ID", id);
    sessionStorage.setItem("SIM_TYPE", "1");
    navigate("/terminal_traffic_stats");
  }
  const emptyRows =
    pageState.page > 0
      ? Math.max(
          0,
          (1 + pageState.page) * pageState.rowsPerPage - deviceList.length
        )
      : 0;
  const handleCheck = (
    event: { target: { checked: boolean; value: string } },
    id: string
  ) => {
    var updatedList = { ...pageState.checked };
    event.target.checked
      ? void (updatedList[id] = true)
      : delete updatedList[id];
    setPageState(cache.value.pageState = { ...pageState, checked: updatedList });
  };
  const handleCheckAll = (
    event: React.MouseEvent<HTMLButtonElement>,
    checkedSome: boolean
  ) => {
    var updatedList: typeof pageState.checked = {};
    if ((event.target as HTMLInputElement).checked && !checkedSome) {
      deviceList.map((d) => (updatedList[d.id] = true));
    }
    setPageState(cache.value.pageState = { ...pageState, checked: updatedList });
  };
  const checkedSome = () => {
    const count = deviceList.length;
    const selected = deviceList.filter((d) => pageState.checked[d.id]).length;
    return selected > 0 && selected < count;
  };
  const checkedAll = () => {
    const count = deviceList.length;
    return count === deviceList.filter((d) => pageState.checked[d.id]).length;
  };

  /***************************************************************
   * レイアウト
   **************************************************************/
  return (
    <Box sx={{ p: 2 }}>
      <TableContainer>
        <Table sx={{ width: "1670px" }}>
          <Head
            order={pageState.order}
            orderBy={pageState.orderBy}
            onRequestSort={handleRequestSort}
            checkedSome={checkedSome()}
            checkedAll={checkedAll() && deviceList.length > 0}
            disabled={deviceList.length === 0}
            handleCheckAll={handleCheckAll}
          />
          <TableBody>
            {sliceOnePage(
              stableSort(deviceList, getComparator(pageState.order, pageState.orderBy)),
              pageState
            ).map((item, index) => (
              <TableRow key={index} sx={style.tableRow}>
                <TableCell style={{ position: "sticky", left: 0 }}>
                  <Checkbox
                    {...label}
                    checked={pageState.checked[item.id] ?? false}
                    onChange={(e) => handleCheck(e, item.id)}
                  />
                </TableCell>
                <TableCell sx={{maxWidth: 200, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {item.tag.map((tag) => tag.tagName).join(", ")}
                </TableCell>
                <TableCell sx={{maxWidth: 100, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.label}</TableCell>
                {/** TODO: テナント名の追加に伴うレイアウトの再考 */}
                <TableCell sx={{minWidth: 124, maxWidth: 124, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>{item.imei}</TableCell>
                <TableCell sx={{minWidth: 116, maxWidth: 116, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>{item.telNumber}</TableCell>
                <TableCell sx={{maxWidth: 60, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>{item.model}</TableCell>
                <TableCell sx={{maxWidth: 60, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>{item.deviceName}</TableCell>
                <TableCell sx={{maxWidth: 30, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                  <SimTypeBox
                    isBold
                    type={item.simType}
                    fontSize={contentFontSize}
                  />
                </TableCell>
                <TableCell sx={{maxWidth: 60, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>{DeviceUiConverter.getCarrierColoredString(item.carrier, undefined, contentFontSize)}</TableCell>
                <TableCell sx={{maxWidth: 60, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>{DeviceUiConverter.getNetworkStatusIcon(item.isOnline)}</TableCell>
                <TableCell sx={{maxWidth: 85, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>{item.deviceUser}</TableCell>
                <TableCell sx={{maxWidth: 60, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}><Link sx={{ cursor: "pointer"}} onClick={() => simTrafficClick(item.id)}>{+item.simTraffic / 1000} MB</Link></TableCell>
                <TableCell sx={{maxWidth: 60, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>{item.model !== "FS045U" ? "非対応" : <Link sx={{ cursor: "pointer"}} onClick={() => sim2TrafficClick(item.id)}>{(isNaN(+item.sim2Traffic) ? 0 : +item.sim2Traffic) / 1000} MB</Link>}</TableCell>
                <TableCell sx={{maxWidth: 60, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>{item.model === "FS010M" ? "非対応" : <Link sx={{ cursor: "pointer"}} onClick={() => esimTrafficClick(item.id, item.model)}>{(isNaN(+item.esimTraffic) ? 0 : +item.esimTraffic) / 1000} MB</Link>}</TableCell>
                <TableCell sx={{minWidth: 113, maxWidth: 113, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>{item.firmware}</TableCell>
                <TableCell sx={{maxWidth: 120, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>{item.tenantName}</TableCell>
                <TableCell sx={{maxWidth: 80}}>{Utility.dateFormatYmdHis(item.createDate)}</TableCell>
                <TableCell style={{ position: "sticky", right: 0, width: 55 }}>
                  <Button
                    variant="contained"
                    onClick={() => detailBtnClick(item.id)}
                  >
                    詳細
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {0 === deviceList.length && (
              <TableRow>
                <TableCell colSpan={15}>{Constants.W_MSG_NO_DATA}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {0 < deviceList.length && (
        <TablePagination
          rowsPerPageOptions={[15, 30, 50]}
          component="div"
          count={deviceList.length}
          rowsPerPage={pageState.rowsPerPage}
          page={pageState.page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </Box>
  );
});
